import { useEffect, useRef, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

const formatarCodigoVisual = (valor) => {
  const s = String(valor || "").replace(/\s+/g, "");
  if (!s) return "";

  const grupos = [];
  let i = s.length;

  while (i > 0) {
    const ini = Math.max(0, i - 6);
    grupos.unshift(s.slice(ini, i));
    i = ini;
  }

  return grupos.join(" ");
};

export default function DialogBarcodeReader({ open, onClose }) {
  const { showAlert } = useAlert();

  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [codigo, setCodigo] = useState("");
  const [tipoCodigo, setTipoCodigo] = useState("");
  const [resultado, setResultado] = useState(null);
  const [suportaDetector, setSuportaDetector] = useState(false);

  const [materiais, setMateriais] = useState([]);
  const [materialSelecionado, setMaterialSelecionado] = useState(null);
  const [materialInput, setMaterialInput] = useState("");
  const [quantidadeAssoc, setQuantidadeAssoc] = useState("1");
  const [associarLoading, setAssociarLoading] = useState(false);

  const [modoOperacao, setModoOperacao] = useState("");
  const [quantidadeMov, setQuantidadeMov] = useState("1");
  const [documento, setDocumento] = useState("");
  const [localizacao, setLocalizacao] = useState("");

  const carregarMateriais = async (texto) => {
    const q = (texto || "").trim();

    if (q.length < 2) {
      setMateriais([]);
      return;
    }

    const [data, err] = await handleApi(
      api.post("Pesquisa/QuickSearch", {
        q,
        limit: 30,
        refvendedor: "",
      })
    );

    if (err) {
      setMateriais([]);
      return;
    }

    const lista = Array.isArray(data)
      ? data.filter((x) => String(x?.kind || "").toLowerCase() === "material")
      : [];

    setMateriais(lista);
  };

  const normalizarQuantidade = (valor) => {
    const txt = String(valor || "")
      .replace(",", ".")
      .trim();
    const n = Number(txt);
    return Number.isFinite(n) && n > 0 ? n : 1;
  };

  const pararCamera = () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current) videoRef.current.srcObject = null;
    } catch {}
  };

  const limpar = () => {
    setCodigo("");
    setTipoCodigo("");
    setResultado(null);
    setLoading(false);
    setCameraLoading(false);
    setMateriais([]);
    setMaterialSelecionado(null);
    setMaterialInput("");
    setQuantidadeAssoc("1");
    setAssociarLoading(false);
    setModoOperacao("");
    setQuantidadeMov("1");
    setDocumento("");
    setLocalizacao("");
  };

  const fechar = () => {
    pararCamera();
    limpar();
    onClose?.();
  };

  const iniciarCamera = async () => {
    setCameraLoading(true);

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        showAlert("Este browser não permite acesso à câmara neste contexto.", "warning");
        setCameraLoading(false);
        return;
      }

      pararCamera();

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err) {
      console.error(err);
      showAlert("Não foi possível iniciar a câmara.", "error");
    } finally {
      setCameraLoading(false);
    }
  };

  const pesquisarCodigo = async (codigoArg = null, tipoArg = null) => {
    const cod = (codigoArg ?? codigo ?? "").trim();
    const tipo = (tipoArg ?? tipoCodigo ?? "").trim();

    if (!cod) {
      showAlert("Indica ou lê um código.", "warning");
      return;
    }

    setLoading(true);
    setResultado(null);

    const [data, err, meta] = await handleApi(
      api.post("Materiais/SelMaterialIdentificadorPorCodigo", {
        codigo: cod,
        tipo_codigo: tipo,
      })
    );

    setLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    setResultado(data || null);

    if (data) {
      setMaterialSelecionado(null);
      setMaterialInput("");
      setMateriais([]);
      setQuantidadeAssoc("1");
      setModoOperacao("");
      setQuantidadeMov(String(data.quantidade || 1));
      setDocumento("");
      setLocalizacao("");
    }

    if (meta?.message) {
      showAlert(meta.message, meta.severity || "success");
    }
  };

  const associarCodigoMaterial = async () => {
    const cod = (codigo || "").trim();

    if (!cod) {
      showAlert("Lê ou indica primeiro um código.", "warning");
      return;
    }

    const refmaterial = String(
      materialSelecionado?.refmaterial || materialSelecionado?.ref || ""
    ).trim();

    if (!refmaterial) {
      showAlert("Seleciona o material a associar.", "warning");
      return;
    }

    const quantidade = normalizarQuantidade(quantidadeAssoc);

    setAssociarLoading(true);

    const [data, err, meta] = await handleApi(
      api.post("Materiais/InsMaterialIdentificador", {
        codigo: cod,
        tipo_codigo: (tipoCodigo || "").trim(),
        refmaterial,
        quantidade,
        descricao: materialSelecionado?.title || materialSelecionado?.nome || "",
        origem: "LEITOR_NAVBAR",
      })
    );

    setAssociarLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    if (meta?.message) {
      showAlert(meta.message, meta.severity || "success");
    }

    await pesquisarCodigo(cod, tipoCodigo);
  };

  const corrigirAssociacao = async () => {
    if (!resultado?.id) {
      showAlert("Não foi encontrada uma associação ativa para corrigir.", "warning");
      return;
    }

    setAssociarLoading(true);

    const [data, err, meta] = await handleApi(
      api.post("Materiais/UpdMaterialIdentificador", {
        id: resultado.id,
        quantidade: resultado.quantidade || 1,
        descricao: resultado.descricao || "",
        inativo: 1,
      })
    );

    setAssociarLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    if (meta?.message) {
      showAlert("Associação anterior inativada. Escolhe agora o material correto.", "warning");
    }

    setResultado(null);
    setMaterialSelecionado(null);
    setMaterialInput("");
    setMateriais([]);
    setQuantidadeAssoc(String(resultado.quantidade || 1));
  };

  const capturarELer = async () => {
    try {
      if (!("BarcodeDetector" in window)) {
        showAlert("Este browser não suporta leitura nativa de códigos.", "warning");
        return;
      }

      const video = videoRef.current;

      if (!video || !video.videoWidth || !video.videoHeight) {
        showAlert("A imagem da câmara ainda não está disponível.", "warning");
        return;
      }

      const detector = new window.BarcodeDetector({
        formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "itf", "qr_code"],
      });

      const resultados = await detector.detect(video);

      if (!resultados || resultados.length === 0) {
        showAlert("Não foi detetado nenhum código nesta imagem.", "warning");
        return;
      }

      const cod = resultados[0].rawValue || "";
      const tipo = resultados[0].format || "";

      setCodigo(cod);
      setTipoCodigo(tipo);

      setResultado(null);

      setMaterialSelecionado(null);
      setMaterialInput("");
      setMateriais([]);
      setQuantidadeAssoc("1");

      showAlert(`Código detetado: ${cod}`, "success");

      await pesquisarCodigo(cod, tipo);
    } catch (err) {
      console.error(err);
      showAlert("Erro ao tentar ler o código.", "error");
    }
  };

  useEffect(() => {
    if (!open) return undefined;

    setSuportaDetector("BarcodeDetector" in window);
    iniciarCamera();

    return () => {
      pararCamera();
    };
  }, [open]);

  return (
    <Dialog open={open} onClose={fechar} maxWidth="sm" fullWidth>
      <DialogTitle>Leitor de código</DialogTitle>

      <DialogContent dividers>
        {(loading || cameraLoading) && (
          <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
            <CircularProgress size={28} />
          </MDBox>
        )}

        <MDBox display="grid" gap={1.5}>
          <MDTypography variant="button" color={suportaDetector ? "success" : "error"}>
            BarcodeDetector: {suportaDetector ? "suportado" : "não suportado neste browser"}
          </MDTypography>

          <video
            ref={videoRef}
            muted
            playsInline
            autoPlay
            style={{
              width: "100%",
              borderRadius: 8,
              background: "#000",
              minHeight: 220,
              maxHeight: 340,
              objectFit: "cover",
            }}
          />

          <MDTypography variant="button" color="text">
            Aponta a câmara ao código e carrega em “Capturar e ler”.
          </MDTypography>

          <MDButton color="info" onClick={capturarELer} disabled={loading || cameraLoading}>
            Capturar e ler
          </MDButton>

          <MDBox>
            <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Código lido</div>
            <TextField
              size="small"
              fullWidth
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.replace(/\s+/g, ""))}
              placeholder="Ex.: 1234567890123"
            />

            {codigo ? (
              <div
                style={{
                  marginTop: 6,
                  padding: "8px 10px",
                  borderRadius: 8,
                  background: "#f8f9fa",
                  fontSize: 20,
                  fontWeight: 700,
                  letterSpacing: 1,
                  color: "#1a2b4c",
                  textAlign: "center",
                }}
              >
                {formatarCodigoVisual(codigo)}
              </div>
            ) : null}
          </MDBox>

          <MDBox>
            <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Tipo de código</div>
            {tipoCodigo ? (
              <div
                style={{
                  marginTop: 6,
                  padding: "8px 10px",
                  borderRadius: 8,
                  background: "#f8f9fa",
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#1a2b4c",
                  textAlign: "center",
                }}
              >
                {tipoCodigo}
              </div>
            ) : (
              <TextField
                size="small"
                fullWidth
                value=""
                disabled
                placeholder="Será preenchido automaticamente"
              />
            )}
          </MDBox>

          {resultado ? (
            <MDBox p={1.5} borderRadius="8px" style={{ background: "#f8f9fa" }}>
              <MDTypography variant="button" fontWeight="bold">
                Material encontrado
              </MDTypography>

              <MDTypography variant="caption" display="block">
                Ref.: {resultado.refmaterial}
              </MDTypography>

              <MDTypography variant="caption" display="block">
                Nome: {resultado.nomematerial || ""}
              </MDTypography>

              <MDTypography variant="caption" display="block">
                Quantidade sugerida: {resultado.quantidade || 1}
              </MDTypography>

              <MDBox mt={1.5}>
                <MDButton
                  color="warning"
                  variant="outlined"
                  onClick={corrigirAssociacao}
                  disabled={associarLoading}
                >
                  Corrigir associação
                </MDButton>
              </MDBox>

              <MDBox mt={2} display="flex" gap={1} flexWrap="wrap">
                <MDButton
                  color={modoOperacao === "inventario" ? "info" : "secondary"}
                  variant={modoOperacao === "inventario" ? "gradient" : "outlined"}
                  onClick={() => setModoOperacao("inventario")}
                >
                  Inventário
                </MDButton>

                <MDButton
                  color={modoOperacao === "conferencia" ? "info" : "secondary"}
                  variant={modoOperacao === "conferencia" ? "gradient" : "outlined"}
                  onClick={() => setModoOperacao("conferencia")}
                >
                  Conferência
                </MDButton>
              </MDBox>

              {modoOperacao ? (
                <MDBox mt={2} display="grid" gap={1.25}>
                  {modoOperacao === "conferencia" && (
                    <MDBox>
                      <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>
                        Documento
                      </div>
                      <TextField
                        size="small"
                        fullWidth
                        value={documento}
                        onChange={(e) => setDocumento(e.target.value)}
                        placeholder="Ex.: guia, fatura, documento interno"
                      />
                    </MDBox>
                  )}

                  <MDBox>
                    <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>
                      Quantidade
                    </div>
                    <TextField
                      size="small"
                      fullWidth
                      value={quantidadeMov}
                      onChange={(e) => setQuantidadeMov(e.target.value)}
                      placeholder="Quantidade"
                    />
                  </MDBox>

                  <MDBox>
                    <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>
                      Localização
                    </div>
                    <TextField
                      size="small"
                      fullWidth
                      value={localizacao}
                      onChange={(e) => setLocalizacao(e.target.value)}
                      placeholder="Ex.: Armazém / corredor / estante / prateleira"
                    />
                  </MDBox>

                  <MDButton color="success" disabled>
                    {modoOperacao === "inventario"
                      ? "Registar inventário (em breve)"
                      : "Registar conferência (em breve)"}
                  </MDButton>
                </MDBox>
              ) : null}
            </MDBox>
          ) : codigo ? (
            <MDBox p={1.5} borderRadius="8px" style={{ background: "#fff7ed" }}>
              <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
                Código ainda não associado
              </MDTypography>

              <MDBox display="grid" gap={1.25}>
                <MDBox>
                  <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>Material</div>
                  <Autocomplete
                    size="small"
                    filterOptions={(options) => options}
                    options={materiais}
                    value={materialSelecionado}
                    inputValue={materialInput}
                    onInputChange={(_, value) => {
                      setMaterialInput(value);
                      carregarMateriais(value);
                    }}
                    onChange={(_, value) => setMaterialSelecionado(value)}
                    getOptionLabel={(option) =>
                      option?.label ||
                      option?.title ||
                      option?.nome ||
                      `${option?.ref || option?.refmaterial || ""}`
                    }
                    isOptionEqualToValue={(option, value) =>
                      String(option?.ref || option?.refmaterial || "") ===
                      String(value?.ref || value?.refmaterial || "")
                    }
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Pesquisar material..." />
                    )}
                  />
                </MDBox>

                <MDBox>
                  <div style={{ fontSize: 12, color: "#7b809a", marginBottom: 4 }}>
                    Quantidade representada por este código
                  </div>
                  <TextField
                    size="small"
                    fullWidth
                    value={quantidadeAssoc}
                    onChange={(e) => setQuantidadeAssoc(e.target.value)}
                    placeholder="Ex.: 1, 6, 24"
                  />
                </MDBox>

                <MDButton
                  color="success"
                  onClick={associarCodigoMaterial}
                  disabled={associarLoading}
                >
                  {associarLoading ? "A associar..." : "Associar código ao material"}
                </MDButton>
              </MDBox>
            </MDBox>
          ) : null}
        </MDBox>
      </DialogContent>

      <DialogActions>
        <MDButton onClick={fechar} color="secondary">
          Fechar
        </MDButton>
        <MDButton onClick={() => pesquisarCodigo()} color="info" disabled={loading}>
          Pesquisar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
